import React, { useRef, useEffect } from "react";
import cx from "classnames";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier } from "dnd-core";
import { getEmptyImage } from "react-dnd-html5-backend";

import { getTestAttributes } from "@common/utils/tests";
import {
  SkipLogicCondition,
  SkipLogicConditionCreateDto,
} from "@common/components/skip-logic/skip-logic.types";
import { Cell } from "./components/cell";

import "./row.styles.scss";

type RowColumns = Array<{
  label: string;
  fieldName: keyof SkipLogicConditionCreateDto;
  component: (condition: SkipLogicCondition) => React.ReactNode;
}>;

type RowProps = {
  condition: SkipLogicCondition;
  conditionIndex: number;
  onClick: (condition: SkipLogicCondition) => void;
  selectedRow: boolean;
  columns: RowColumns;
  enableDragDrop?: boolean;
  onRowDrop?: (dragIndex: number, hoverIndex: number) => void;
};

type DragItem = {
  id: string | number;
  index: number;
  type: string;
};

type RowViewProps = {
  condition: SkipLogicCondition;
  conditionIndex: number;
  selectedRow: boolean;
  columns: RowColumns;
  onClick: () => void;
  isDragging?: boolean;
  handlerId?: Identifier | null;
  rowRef?: React.Ref<HTMLTableRowElement>;
};

const RowView: React.FC<RowViewProps> = ({
  condition,
  conditionIndex,
  selectedRow,
  columns,
  onClick,
  isDragging = false,
  handlerId,
  rowRef,
}) => {
  return (
    <tr
      ref={rowRef}
      className={cx("SkipLogic__row", {
        ["SkipLogic__row--selected"]: selectedRow,
        ["SkipLogic__row--dragging"]: isDragging,
      })}
      {...getTestAttributes({
        id: `pan-query-fields-data-tbody-row${conditionIndex}`,
      })}
      key={condition.id}
      onClick={onClick}
      data-handler-id={handlerId}
    >
      <Cell
        testAttributes={getTestAttributes({
          id: `pan-query-fields-data-tbody-row${conditionIndex}-cell0`,
        })}
      >
        <img src={condition.field.imageURL} height={20} />
      </Cell>

      {columns.map((column, columnIndex) => (
        <Cell
          key={column.fieldName}
          testAttributes={getTestAttributes({
            id: `pan-query-fields-data-tbody-row${conditionIndex}-cell${columnIndex + 1}`,
          })}
        >
          {column.component(condition)}
        </Cell>
      ))}
    </tr>
  );
};

const DraggableRow: React.FC<RowProps> = ({
  condition,
  onClick,
  columns,
  conditionIndex,
  selectedRow,
  enableDragDrop = false,
  onRowDrop,
}) => {
  const handleClick = React.useCallback(() => {
    onClick(condition);
  }, [onClick, condition]);

  const ref = useRef<HTMLTableRowElement>(null);

  const hasParentField = condition.parentField != null;
  const parentFieldName =
    condition.parentField?.name ??
    (condition.parentField as { fieldname?: string })?.fieldname ??
    null;
  const itemType = hasParentField
    ? `SKIP_LOGIC_ROW_WITH_PARENT_${parentFieldName}`
    : "SKIP_LOGIC_ROW_WITHOUT_PARENT";

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: itemType,
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    drop(draggedItem) {
      if (!ref.current || !onRowDrop) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = conditionIndex;

      if (dragIndex === hoverIndex) {
        return;
      }

      onRowDrop(dragIndex, hoverIndex);
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: itemType,
    item: () => ({ id: condition.id, index: conditionIndex }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => enableDragDrop,
  });

  useEffect(() => {
    if (enableDragDrop) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [preview, enableDragDrop]);

  if (enableDragDrop && onRowDrop) {
    drag(drop(ref));
  }

  return (
    <RowView
      condition={condition}
      conditionIndex={conditionIndex}
      selectedRow={selectedRow}
      columns={columns}
      onClick={handleClick}
      isDragging={isDragging}
      handlerId={handlerId}
      rowRef={ref}
    />
  );
};

type StaticRowProps = Omit<RowProps, "enableDragDrop" | "onRowDrop">;

const StaticRow: React.FC<StaticRowProps> = ({
  condition,
  onClick,
  columns,
  conditionIndex,
  selectedRow,
}) => {
  const handleClick = React.useCallback(() => {
    onClick(condition);
  }, [onClick, condition]);

  return (
    <RowView
      condition={condition}
      conditionIndex={conditionIndex}
      selectedRow={selectedRow}
      columns={columns}
      onClick={handleClick}
    />
  );
};

export const Row: React.FC<RowProps> = (props) => {
  const { enableDragDrop, onRowDrop, ...rest } = props;

  if (enableDragDrop && onRowDrop) {
    return <DraggableRow {...rest} enableDragDrop={enableDragDrop} onRowDrop={onRowDrop} />;
  }

  return <StaticRow {...rest} />;
};
